import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useCart } from '../lib/cart';

type DownloadInfo = {
  productId?: string;
  productName?: string;
  downloadUrl?: string;
  downloadFilename?: string;
};

type SessionInfo = {
  status: string;
  productName?: string;
  customerEmail?: string;
  downloadUrl?: string;
  downloadFilename?: string;
  downloads?: DownloadInfo[];
  licenseKeys?: string[];
  portalUrl?: string | null;
  portalUserName?: string | null;
  mock?: boolean;
  free?: boolean;
  message?: string;
};

export function SuccessPage() {
  const [params] = useSearchParams();
  const sessionId = params.get('session_id');
  const { clear } = useCart();
  const [info, setInfo] = useState<SessionInfo | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!sessionId) {
      setError('Missing checkout session id.');
      setLoading(false);
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`/api/checkout-session?session_id=${encodeURIComponent(sessionId)}`);
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'Unable to verify payment');
        if (!cancelled) {
          setInfo(data);
          clear();
        }
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Verification failed');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [sessionId, clear]);

  const downloads =
    info?.downloads && info.downloads.length
      ? info.downloads
      : info?.downloadUrl
        ? [{ productName: info.productName, downloadUrl: info.downloadUrl, downloadFilename: info.downloadFilename }]
        : [];

  return (
    <div className="container">
      <div className="card checkout-panel">
        <div className={`status-icon ${error ? 'warn' : 'ok'}`}>
          <span className="material-symbols-outlined">{error ? 'error' : 'verified'}</span>
        </div>
        <h1>{error ? 'Almost there' : 'Payment confirmed'}</h1>

        {loading && <p>Verifying your Stripe™ session…</p>}

        {error && (
          <>
            <p>{error}</p>
            <Link className="btn btn-primary" to="/marketplace">
              Return to Marketplace
            </Link>
          </>
        )}

        {!loading && !error && info && (
          <>
            <p>
              {info.free
                ? 'No charge today. Your license is ready — grab the download below.'
                : info.mock
                  ? 'Mock checkout complete (local demo mode).'
                  : 'Your purchase is verified. We emailed your Extensions Portal login, license key, and a backup download link.'}
            </p>
            {info.productName && (
              <p>
                <strong>{info.productName}</strong>
                {info.customerEmail ? ` · receipt → ${info.customerEmail}` : ''}
              </p>
            )}

            {(info.portalUrl || info.portalUserName) && (
              <div className="download-box">
                <h3>Extensions Portal</h3>
                <p>
                  {info.portalUserName ? (
                    <>
                      Username: <strong>{info.portalUserName}</strong>
                      <br />
                    </>
                  ) : null}
                  Your password is in the confirmation email. Sign in to view licenses and downloads anytime.
                </p>
                {info.portalUrl && (
                  <a className="btn btn-primary" href={info.portalUrl} target="_blank" rel="noreferrer">
                    Open portal
                  </a>
                )}
              </div>
            )}

            {info.licenseKeys && info.licenseKeys.length > 0 && (
              <div className="download-box">
                <h3>License key{info.licenseKeys.length > 1 ? 's' : ''}</h3>
                {info.licenseKeys.map((key) => (
                  <p className="license-key" key={key}>
                    <code>{key}</code>
                  </p>
                ))}
              </div>
            )}

            {downloads.length ? (
              downloads.map((row) => (
                <div className="download-box" key={row.downloadUrl || row.productName}>
                  <h3>Secure download{row.productName ? ` — ${row.productName}` : ''}</h3>
                  <p>
                    File: <strong>{row.downloadFilename || 'extension.zip'}</strong>
                    <br />
                    Install via EspoCRM → Administration → Extensions.
                  </p>
                  {row.downloadUrl && (
                    <a className="btn btn-primary" href={row.downloadUrl}>
                      Download extension
                    </a>
                  )}
                </div>
              ))
            ) : (
              <p className="muted">
                {info.message ||
                  'Download link is being prepared. Check your email for a backup link, or refresh in a moment.'}
              </p>
            )}

            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'center', flexWrap: 'wrap' }}>
              <Link className="btn btn-ghost" to="/marketplace">
                Back to Marketplace
              </Link>
              <Link className="btn btn-primary" to="/">
                Home
              </Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
